import './CopHomePageCard.scss'

import Icon from '@react/Atoms/Icon'

import { COP_COMMON_ERROR_MESSAGES } from '../../constants/common-error-messages'
import { ToastService } from '../../services/toast.service'
import { useSectionPermissions } from '../Shared/PermissionAccessGuard/hooks/useSectionPermissions'

export type FeatureItem = {
  title: string
  description?: string
  anchorLink?: string
  icon?: string
}

export type CopHomePageCardProps = {
  itemsjson?: string
}

type RawFeatureItem = FeatureItem & {
  anchorlink?: string
  link?: string
  url?: string
}

const DEFAULT_ICON = 'chatMessage'
const CSP_QUICK_ACCESS_TILE_ID = 'feature-item-0'
const PSA_QUICK_ACCESS_TILE_ID = 'feature-item-2'

export default function CopHomePageCard({ itemsjson }: CopHomePageCardProps) {
  const items = parseItems(itemsjson)
  const { permissions, isLoading } = useSectionPermissions()
  const canViewCSPQuickAccessTile = !isLoading && permissions.canViewCSPQuickAccessTile
  const canViewPSAQuickAccessTile = !isLoading && permissions.canViewPSAQuickAccessTile
  const itemsWithIds = items.map((item, index) => ({ item, id: getFeatureItemId(index) }))
  const visibleItems = itemsWithIds.filter(({ id }) => isVisibleFeatureItemId(id, canViewCSPQuickAccessTile, canViewPSAQuickAccessTile))

  return (
    <div className="cmp-featurelist card-component">
      {visibleItems.length === 0 && <div style={{ color: '#999', padding: '10px', border: '1px solid #ccc' }}>No items configured</div>}
      {visibleItems.length > 0 && (
        <ul className="cmp-featurelist__items">
          {visibleItems.map(({ item, id }) => (
            <li key={id} id={id} className="cmp-featurelist__item">
              <FeatureCard item={item} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function getFeatureItemId(index: number): string {
  return `feature-item-${index}`
}

function isVisibleFeatureItemId(id: string, canViewCSPQuickAccessTile: boolean, canViewPSAQuickAccessTile: boolean): boolean {
  if (id === CSP_QUICK_ACCESS_TILE_ID) {
    return canViewCSPQuickAccessTile
  }

  if (id === PSA_QUICK_ACCESS_TILE_ID) {
    return canViewPSAQuickAccessTile
  }

  return true
}

function parseItems(itemsjson?: string): FeatureItem[] {
  if (!itemsjson) {
    return []
  }

  const normalized = itemsjson
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&amp;/g, '&')

  try {
    const parsed = JSON.parse(normalized)

    if (Array.isArray(parsed)) {
      return (parsed as RawFeatureItem[]).map(normalizeFeatureItem)
    }

    if (parsed && Array.isArray((parsed as { items?: unknown[] }).items)) {
      return ((parsed as { items: RawFeatureItem[] }).items || []).map(normalizeFeatureItem)
    }

    return []
  } catch (e) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['home-page-card-parsing'],
    )
    const message = e instanceof Error && e.message ? e.message : fallbackMessage
    ToastService.error(message)
    return []
  }
}

function normalizeFeatureItem(item: RawFeatureItem): FeatureItem {
  const rawAnchor = item.anchorLink || item.anchorlink || item.link || item.url
  const trimmedAnchor = typeof rawAnchor === 'string' ? rawAnchor.trim() : ''

  const anchorLink = trimmedAnchor && trimmedAnchor !== '#' ? trimmedAnchor : undefined

  const rawIcon = typeof item.icon === 'string' ? item.icon.trim() : ''
  const icon = rawIcon || undefined

  return {
    title: item.title,
    description: item.description,
    anchorLink,
    icon,
  }
}

function FeatureCard({ item }: { item: FeatureItem }) {
  return (
    <div className="cmp-featurelist__card">
      <div className="cmp-featurelist__content-row">
        <div className="cmp-featurelist__text">
          <div className="cmp-featurelist__leading-icon" aria-hidden="true">
            <Icon iconName={item.icon || DEFAULT_ICON} />
          </div>
          <div>
            {item.title && <h3 className="cmp-featurelist__title">{item.title}</h3>}
            {item.description && <p className="cmp-featurelist__description">{item.description}</p>}
          </div>
        </div>
      </div>

      {item.anchorLink && (
        <div className="cmp-featurelist__icon-row">
          <a href={item.anchorLink} className="cmp-featurelist__icon-link" aria-label={`Open ${item.title || 'feature'}`} target="_blank" rel="noopener noreferrer">
            <p className="cmp-featurelist__link">
              <span className="cmp-featurelist__link-icon cmp-button__icon icon-keyboard_arrow_right" aria-hidden="true"></span>
            </p>
          </a>
        </div>
      )}
    </div>
  )
}